import type { Task } from "@app/core";
import { useEffect, useRef, useState } from "react";

import { chordFromEvent } from "../../lib/shortcut-chord.js";
import { useSettingsStore } from "../../stores/settings.store.js";

export interface AgendaNavRow {
  /** Stable per-row key (also the row's `data-agenda-row` value). */
  key: string;
  task: Task;
}

/** Day-specific row identity. Multi-day all-day tasks can render in more than
 * one section with the same task id, so the date must participate in the key. */
export function dayAgendaNavKey(date: Date, taskId: string): string {
  return `d:${date.getTime()}:${taskId}`;
}

/** Events are deliberately not completable anywhere in the agenda. */
export function canToggleAgendaRow(task: Task): boolean {
  return !task.is_event;
}

/** Commands that must fire once per physical key press. Arrow navigation is
 * deliberately absent so holding an arrow can still walk the agenda. */
export function isAgendaSingleFireKey(key: string): boolean {
  return key === "Enter" || key === " " || key === "x" || key === "X" || key === "s" || key === "S";
}

interface Handlers {
  toggle: (task: Task, done: boolean) => void;
  edit: (task: Task) => void;
  snooze: (task: Task, pos: { x: number; y: number }) => void;
}

/**
 * Today-screen keyboard triage: ↑/↓ walk the rows (overdue first, then the
 * agenda days), Enter edits, Space/X toggles task completion, S opens the snooze menu at
 * the focused row, Escape drops focus. Inert while typing in a field or while
 * a dialog is open (same guards as the global shortcuts), and it yields to any
 * chord the user has bound globally, so a rebind can never be shadowed.
 * Returns the focused row key for the rows' highlight ring.
 */
export function useAgendaKeyboardNav(rows: AgendaNavRow[], handlers: Handlers): string | null {
  const [focusedKey, setFocusedKey] = useState<string | null>(null);
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;
  // Mirror the key into a ref so the mount-once listener reads the live value.
  const focusedKeyRef = useRef(focusedKey);
  focusedKeyRef.current = focusedKey;
  const lastIndexRef = useRef(-1);

  // Rows shrink when items complete/snooze away — keep the focus in range.
  useEffect(() => {
    setFocusedKey((key) => {
      const current = key ? rows.findIndex((row) => row.key === key) : -1;
      if (current >= 0) {
        lastIndexRef.current = current;
        return key;
      }
      if (!key || rows.length === 0) {
        lastIndexRef.current = -1;
        return null;
      }
      const index = Math.min(Math.max(0, lastIndexRef.current), rows.length - 1);
      lastIndexRef.current = index;
      return rows[index]!.key;
    });
  }, [rows]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const target = e.target as HTMLElement | null;
      const inField = !!target && (
        target.tagName === "INPUT" || target.tagName === "TEXTAREA" ||
        target.tagName === "SELECT" || target.isContentEditable
      );
      const inControl = !!target?.closest(
        'button, a[href], [role="button"], [role="checkbox"], [role="menuitem"], [contenteditable="true"]',
      );
      if (inField || inControl || document.querySelector('[role="dialog"], [role="menu"]')) return;
      // A user-bound global shortcut owns its chord, including an arrow key.
      const bound = Object.values(useSettingsStore.getState().shortcuts);
      if (bound.includes(chordFromEvent(e))) return;

      const list = rowsRef.current;
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        if (list.length === 0) return;
        e.preventDefault();
        const current = list.findIndex((row) => row.key === focusedKeyRef.current);
        const next = e.key === "ArrowDown"
          ? Math.min(list.length - 1, current + 1)
          : Math.max(0, current < 0 ? 0 : current - 1);
        const nextKey = list[next]!.key;
        lastIndexRef.current = next;
        focusedKeyRef.current = nextKey;
        setFocusedKey(nextKey);
        scrollRowIntoView(nextKey);
        return;
      }
      if (e.key === "Escape") {
        lastIndexRef.current = -1;
        focusedKeyRef.current = null;
        setFocusedKey(null);
        return;
      }

      const row = list.find((candidate) => candidate.key === focusedKeyRef.current);
      if (!row) return;
      // A held Space/X used to complete, reopen, and complete the same task in
      // rapid succession (with duplicate writes, undo snapshots and effects).
      // Enter/S could likewise reopen an editor/menu repeatedly.
      if (e.repeat && isAgendaSingleFireKey(e.key)) return;
      const h = handlersRef.current;
      if ((e.key === " " || e.key === "x" || e.key === "X") && canToggleAgendaRow(row.task)) {
        e.preventDefault();
        h.toggle(row.task, row.task.status !== "done");
      } else if (e.key === "Enter") {
        e.preventDefault();
        h.edit(row.task);
      } else if ((e.key === "s" || e.key === "S") && row.task.status !== "done" && !row.task.is_event) {
        e.preventDefault();
        const el = rowElement(row.key);
        const r = el?.getBoundingClientRect();
        h.snooze(row.task, r ? { x: r.right - 48, y: r.bottom } : { x: window.innerWidth / 2, y: window.innerHeight / 2 });
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return focusedKey;
}

function rowElement(key: string | undefined): Element | null {
  return key ? document.querySelector(`[data-agenda-row="${CSS.escape(key)}"]`) : null;
}

function scrollRowIntoView(key: string | undefined): void {
  rowElement(key)?.scrollIntoView({ block: "nearest" });
}
